import { CSSProperties, forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react"

import { Collapse, Flex } from "antd";

import { CollapseItem } from "@hdi-seguros/web-components.collapse";

import { SectionGroupButtonProps, SectionGroupButton, SectionGroupButtonsSkeleton } from "./components";

import './index.css';

interface SectionGroupButtonsProps {
    buttons:                    Array<SectionGroupButtonProps>;
    buttonsJustify?:            CSSProperties['justifyContent'];
    containerButtonsStyle?:     CSSProperties;
    onDesactiveButton:          (id: string) => Promise<void> | void;
    onPressButton:              (id: string) => Promise<void> | void;
    title?:                     string;
    isLoading?:                 boolean;
};

export type SectionGroupButtonsRef = {
    deactivateButtons: () => void;
}

export const SectionGroupButtons = forwardRef<SectionGroupButtonsRef, SectionGroupButtonsProps>(({ 
    title = "", 
    buttons, 
    buttonsJustify,
    onPressButton,
    onDesactiveButton,
    containerButtonsStyle = {},
    isLoading = false
}, ref) => {
    const [activeButtonsIds, setActiveButtonsIds] = useState<Array<string>>([]);

    const onPressSectionGroupButton = useCallback(async (callback: Function, id: string) => {
        setActiveButtonsIds((prevValues) => prevValues.includes(id) ? [] : [id]);
        await callback(id);
    }, [setActiveButtonsIds]);

    const deactivateButtons = useCallback(() => setActiveButtonsIds([]), [setActiveButtonsIds])

    useImperativeHandle(ref, () => ({
        deactivateButtons,
    }))

    const items: Array<CollapseItem> = useMemo(() => [
        {
            key: 'section-group-buttons-items',
            label: title,
            children: (
                <>
                    {
                        isLoading
                            ? <SectionGroupButtonsSkeleton />
                            : (
                                <Flex  
                                    justify={buttonsJustify} 
                                    className="w-100" 
                                    wrap="wrap"
                                    gap="large"
                                    style={containerButtonsStyle}
                                >
                                    {
                                        buttons.map(({ id, title, subTitle, iconName, disabled, isActive, color }, i) => (
                                            <SectionGroupButton
                                                key={`${title}-${subTitle}-${id}-${i}`}
                                                id={id}
                                                title={title}
                                                subTitle={subTitle}
                                                iconName={iconName}
                                                onPress={
                                                    disabled 
                                                        ? undefined
                                                        : () => onPressSectionGroupButton(
                                                            activeButtonsIds.includes(id) ? onDesactiveButton : onPressButton, id
                                                        )
                                                }
                                                disabled={disabled}
                                                isActive={isActive || activeButtonsIds.includes(id)}
                                                color={color}
                                            />
                                    ))
                                    }
                                </Flex>
                            )
                    }
                </>
            )
        }
    ], [
        title, 
        buttonsJustify, 
        buttons, 
        containerButtonsStyle, 
        onPressSectionGroupButton, 
        activeButtonsIds, 
        onDesactiveButton, 
        onPressButton,
        isLoading,
    ]);

    return (
        <div className="section-group-buttons-collapse-container">
            <Collapse defaultActiveKey={"section-group-buttons-items"} items={items} />
        </div>
    )
});
